import React from 'react';
import type {
  LegacyExportDetail,
  LegacyTraceData,
} from '../../services/tracking.service';

/**
 * Legacy (cmsapi/trace.pl → gnosis) trace panel — the legacy parallel to
 * TraceSnapshotPanel. Renders the mapped gnosis fields (already display-
 * formatted by the tracer) + a collapsible Raw Results block, from the
 * [tracejson] payload cmsapi returns.
 */

// gnosis @dataMap CMS column → display label (from gwitraceimport.pl).
const LABELS: Record<string, string> = {
  portoforigin: 'Port of Origin',
  terminalname: 'Terminal',
  portofdischarge: 'Port of Discharge',
  vessel: 'Vessel',
  voyage: 'Voyage',
  vesselsaildateest: 'Est Vessel Sail',
  vesselsaildatetime: 'Act Vessel Sail',
  vesselarrivaldateest: 'Est Vessel Arrival',
  vesselarrivaldatetime: 'Act Vessel Arrival',
  // Export booking receiving window (POHA / APM / gnosis export tracers).
  // Key order here IS the display order: receiving window, then doc cutoff +
  // container count, then the rail pair on its own grid row.
  firstrcvdate: 'First Receive',
  cutoffdatetime: 'Cut Off',
  cutoffdate: 'Cut Off',
  documentcutoffdatetime: 'Doc Cut Off',
  numOfContainers: '# of Containers',
  railfirstrcvdate: 'Rail First Receive',
  railcutoffdate: 'Rail Cut Off',
  arrivalgroundeddatetime: 'Vessel Discharge',
  portterminalname: 'Port Terminal',
  finalDestination: 'Final Dest',
  arrivalgroundedlocation: 'Location',
  traindepartdatetime: 'Train Depart',
  railTerminal: 'Rail Terminal',
  railetadate: 'Rail ETA',
  railarrivaldatetime: 'Rail Arrival',
  readyfordelivery: 'Ready for Delivery',
  availablegoodtogodatetime: 'Available',
  portlfd: 'SSLine LFD',
  lastfreedaydate: 'Terminal LFD',
  outgatedatetime: 'Outgate',
  chassisId: 'Chassis #',
  perdiemlfd: 'Per Diem LFD',
  traceterminatedatetime: 'Trace Terminate',
  customshold: 'Customs Hold',
  customsreldatetime: 'Customs Rls',
  linehold: 'Line Hold',
  otherhold: 'Other Holds',
  gnosislfd: 'Gnosis LFD',
  gnosisperdiemlfd: 'Gnosis Per Diem LFD',
  // CMS-column read-back keys (non-gnosis import tracers: APM / UPRR / Bayport)
  traceruncarrier: 'Trace Carrier',
  tracerundatetime: 'Trace Date',
  traceevent: 'Trace Event',
  tracelocation: 'Trace Location',
  traceeventdatetime: 'Event Date',
  tracevessel: 'Trace Vessel',
  tracevoyage: 'Trace Voyage',
  traceportoforigin: 'POO',
  traceportofdischarge: 'POD',
  customsstatus: 'Customs',
  linestatus: 'Line',
  linereldatetime: 'Line Rls',
  preadvisedatetime: 'Pre Advise',
  hazreqdatetime: 'Haz Req',
  sealnumber: 'Seal',
  etadate: 'ETA',
  chassisid: 'Chassis',
  railbilled: 'Billed',
  status: 'Status',
  destination: 'Destination',
  rampinfo: 'Ramp Info',
  chargesbegin: 'Charges Begin',
};
const ORDER = Object.keys(LABELS);

// Exports load THEN sail, so arrival leads in each row and estimates come
// before actuals (imports keep sail-then-arrival, their chronology). In the
// 2-column grid this positional remap renders:
//   Est Vessel Arrival | Est Vessel Sail
//   Act Vessel Arrival | Act Vessel Sail
const EXPORT_VESSEL_SWAP: Record<string, string> = {
  vesselsaildateest: 'vesselarrivaldateest',
  vesselsaildatetime: 'vesselsaildateest',
  vesselarrivaldateest: 'vesselarrivaldatetime',
  vesselarrivaldatetime: 'vesselsaildatetime',
};

// The vesselsch feed pre-fills its "actual" depart/arrive columns with the
// schedule before the event happens, so an Act vessel date in the future is
// really just the estimate again. Hide it until it has occurred.
const ACT_VESSEL_KEYS = new Set(['vesselsaildatetime', 'vesselarrivaldatetime']);
const isFutureDatetime = (v: unknown): boolean => {
  if (typeof v !== 'string' || v.trim() === '') return false;
  const t = new Date(v.replace(' ', 'T')).getTime();
  return !Number.isNaN(t) && t > Date.now();
};

const label = (k: string): string =>
  LABELS[k] ??
  k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

// HOU-BPT/HOU-BCT (flag-style payloads): v1's popup renders the terminal
// scraper's raw fields directly — the CMS read-back only carries the trace
// columns. Exact raw-key → label map from bpt.pl createHTML, in its display
// order; keys v1 doesn't show stay in the Raw Results block only. "Released
// Thru" reads PORT_PTD — v1's 'Storage Paid Through Day' key is a dead
// old-API leftover (XML element names can't contain spaces).
const RAW_LABELS: Record<string, string> = {
  EQUIPMENTNBR: 'Equipment ID',
  LOCATION: 'Location',
  VESSELETA: 'ETA',
  PORT_LFD: 'Port LFD',
  LINE_LFD: 'Line LFD',
  PORT_PTD: 'Released Thru',
  HOLDS: 'Holds',
  CATEGORY: 'Category',
  SHIPPINGLINE: 'Line',
  STATUS: 'Freight Kind',
  BILLOFLADING: 'BL',
  ISOCODE: 'ISO Type',
  VESSEL: 'Vessel',
  GROSSWEIGHT: 'Cargo Weight (lb)',
  VOYAGE: 'Voyage',
  SEAL1: 'Seal',
  SEAL2: 'Seal 2',
  POL: 'POL',
  POD: 'POD',
  TEMP: 'Temperature (F)',
  HAZ_CLASS: 'Hazardous',
  LOCATIONDETAILS: 'Yard Position',
};
const RAW_ORDER = Object.keys(RAW_LABELS);

// Raw keys whose value already surfaces on a CMS read-back row — skip the raw
// duplicate when that field is present (PORT_LFD renders in Terminal LFD;
// bpt.pl emits portlfd from LINE_LFD, etadate from VESSELETA, etc.).
const RAW_TO_FIELD: Record<string, string> = {
  PORT_LFD: 'lastfreedaydate',
  LINE_LFD: 'portlfd',
  SEAL1: 'sealnumber',
  VESSEL: 'vessel',
  VOYAGE: 'voyage',
  POL: 'portoforigin',
  POD: 'portofdischarge',
  VESSELETA: 'etadate',
  LOCATION: 'tracelocation',
  LOCATIONDETAILS: 'arrivalgroundedlocation',
};

// The terminal-reported hazardous class ('9', '3', …) from a flag-style raw
// payload — non-empty means the terminal flagged the container HAZ. Null when
// absent/blank (not reported hazardous, or a non-flag tracer like gnosis).
export const hazClass = (data: LegacyTraceData | null | undefined): string | null => {
  const raw = data?.raw;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const v = (raw as Record<string, unknown>).HAZ_CLASS;
  const hs = typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';
  return hs || null;
};

const s = (v: unknown): string => (v == null ? '' : String(v));

/** Small header-row table for the export-detail sections (v1 tracewin parity). */
const SectionTable: React.FC<{
  title: string;
  headers: string[];
  rows: (string | number | null | undefined)[][];
}> = ({ title, headers, rows }) => (
  <div className="mt-2">
    <div className="mb-0.5 text-[0.6875rem] font-semibold text-slate-600 dark:text-gray-300">
      {title}
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-[0.6875rem]">
        <thead>
          <tr className="bg-slate-200 text-left text-slate-700 dark:bg-gray-700 dark:text-gray-200">
            {headers.map((h) => (
              <th key={h} className="whitespace-nowrap px-2 py-0.5 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={headers.length}
                className="px-2 py-0.5 italic text-slate-400 dark:text-gray-500"
              >
                none
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-200 dark:border-gray-700">
                {r.map((c, j) => (
                  // No nowrap: datetimes may break onto two lines so all the
                  // C/O columns fit the modal without horizontal scrolling.
                  <td key={j} className="px-2 py-0.5 align-top text-slate-900 dark:text-gray-100">
                    {s(c)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/** The v1 export tracewin popup's sections: booking header, Vessel
 *  Information, Equipment Information, Equipment In For Booking. */
const ExportDetail: React.FC<{ detail: LegacyExportDetail }> = ({ detail }) => {
  const v = detail.vesselinfo;
  const headerRows: [string, string][] = [
    ['Category', s(detail.category)],
    ['Booking', s(detail.booking)],
    ['Line', s(detail.line)],
    ['Hazmat', s(detail.hazmat)],
    ['PreAdvise', s(detail.preadvise)],
    ['PreAdvise Date', s(detail.preadvisedate)],
    ['Notes', s(detail.notes)],
  ];
  return (
    <div className="mt-2 border-t border-slate-200 pt-2 dark:border-gray-700">
      <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {headerRows.map(([l, val]) => (
          <div key={l} className="flex gap-1.5 py-0.5 text-[0.75rem]">
            <span className="min-w-[130px] shrink-0 text-right font-medium text-slate-500 dark:text-gray-400">
              {l}:
            </span>
            <span className="break-words text-slate-900 dark:text-gray-100">{val}</span>
          </div>
        ))}
      </div>
      {v && (
        <SectionTable
          title="Vessel Information"
          headers={['Vessel', 'Voyage', 'Terminal', 'Status', 'POL', 'POD', 'Begin Rcv', 'Dry C/O', 'Haz C/O', 'Reefer C/O']}
          rows={[[v.vessel, v.voyage, v.terminal, v.status, v.pol, v.pod, v.beginrcv, v.drycutoff, v.hazcutoff, v.reefercutoff]]}
        />
      )}
      <SectionTable
        title="Equipment Information"
        headers={['Qty', 'Mty', 'Rcvd', 'Size', 'Type']}
        rows={(detail.equipinfo ?? []).map((e) => [e.qty, e.mty, e.rcvd, e.size, e.type])}
      />
      <SectionTable
        title="Equipment In For Booking"
        headers={['Equipment#', 'Size', 'Type', 'Position']}
        rows={(detail.equipin ?? []).map((e) => [e.equipment, e.size, e.type, e.position])}
      />
    </div>
  );
};

const LegacyTracePanel: React.FC<{ data: LegacyTraceData }> = ({ data }) => {
  const fields = (data.fields ?? {}) as Record<string, unknown>;
  // Show every field the tracer supplied; highlight the ones this trace changed
  // (cmsapi's before/after diff). NOTE: the read-back tracers (UPRR/APM/Bayport)
  // currently return a full CMS read-back, so they also surface pre-existing
  // order fields (vessel/voyage/discharge/available). Dropping those requires
  // scoping the tracer emit to its own columns — a cmstrace/cmsapi change.
  const updated = new Set(data.updated ?? []);
  // Keep empty-string values so gnosis/BPT render their full field grid with
  // blank rows (consistent layout, mirrors the click-track). cmsapi only sends
  // an empty value for a field the tracer actually supplies; only drop keys
  // that are truly absent (null/undefined).
  // export_detail is only emitted by the export tracers — use it as the
  // "this is an export trace" signal for the export-specific display rules.
  const isExport = !!data.export_detail;
  const order = isExport ? ORDER.map((k) => EXPORT_VESSEL_SWAP[k] ?? k) : ORDER;
  const keys = [
    ...order.filter((k) => k in fields),
    ...Object.keys(fields).filter((k) => !(k in LABELS)),
  ].filter(
    (k) =>
      fields[k] != null &&
      !(isExport && ACT_VESSEL_KEYS.has(k) && isFutureDatetime(fields[k])),
  );

  // HOU-BPT/HOU-BCT report availability as a bare YES/NO flag (raw
  // `Available`) — there is no real availability datetime; the CMS column is
  // just the trace-run stamp. Show the flag instead (green YES, like v1).
  const raw =
    data.raw && typeof data.raw === 'object' && !Array.isArray(data.raw)
      ? (data.raw as Record<string, unknown>)
      : null;
  const rawAvail = typeof raw?.Available === 'string' ? raw.Available.trim().toUpperCase() : null;
  const availFlag = rawAvail === 'YES' || rawAvail === 'NO' ? rawAvail : null;
  // Their raw PORT_LFD is the terminal's last free day; no CMS column gets
  // stamped for it, so surface it in the Terminal LFD row.
  const rawPortLfd =
    (typeof raw?.PORT_LFD === 'string' && raw.PORT_LFD.trim()) || null;

  // Flag-style payloads only: append the raw scraper fields v1's popup shows
  // that the read-back grid doesn't already cover (Holds, Category, Freight
  // Kind, …). Everything else stays in the Raw Results block, like v1.
  const rawValue = (k: string): string => {
    const v = raw?.[k];
    const rs = typeof v === 'string' || typeof v === 'number' ? String(v) : '';
    // v1 folds the derived REL/TRAN marker into the Holds line…
    if (k === 'HOLDS' && typeof raw?.reltran === 'string') {
      return `${rs} ${raw.reltran}`.trim();
    }
    // …and prints the weight as sprintf("%.2f").
    if (k === 'GROSSWEIGHT' && rs !== '' && Number.isFinite(Number(rs))) {
      return Number(rs).toFixed(2);
    }
    return rs;
  };
  const rawExtras: [string, string][] =
    availFlag !== null && raw
      ? RAW_ORDER.filter((k) => {
          if (!(k in raw)) return false;
          const fk = RAW_TO_FIELD[k];
          if (!fk || !(fk in fields)) return true;
          if (k === 'PORT_LFD') {
            // Covered by the Terminal LFD row when it fills the blank or
            // matches; when the lesser-of rule stamped LINE_LFD instead,
            // still show Port LFD on its own row (v1 always shows it).
            const flfd = String(fields[fk] ?? '').trim();
            return !!rawPortLfd && flfd !== '' && flfd !== rawPortLfd;
          }
          return false;
        }).map((k): [string, string] => [k, rawValue(k)])
      : [];

  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-2 dark:border-gray-700 dark:bg-gray-900">
      {updated.size > 0 && (
        <div className="mb-1 text-[0.625rem] text-slate-400 dark:text-gray-500">
          Highlighted = updated by this trace
        </div>
      )}
      <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {keys.map((k) => {
          const isAvailFlag = availFlag !== null && k === 'availablegoodtogodatetime';
          const value =
            isAvailFlag
              ? availFlag
              : k === 'lastfreedaydate' && rawPortLfd && !String(fields[k] ?? '').trim()
                ? rawPortLfd
                : String(fields[k]);
          return (
            <div key={k} className="flex gap-1.5 py-0.5 text-[0.75rem]">
              <span className="min-w-[130px] shrink-0 text-right font-medium text-slate-500 dark:text-gray-400">
                {label(k)}:
              </span>
              <span
                className={
                  isAvailFlag
                    ? availFlag === 'YES'
                      ? 'break-words rounded bg-green-200 px-1 font-semibold text-green-900 dark:bg-green-500/30 dark:text-green-100'
                      : 'break-words rounded bg-red-100 px-1 font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-200'
                    : updated.has(k)
                      ? 'break-words rounded bg-yellow-200 px-1 font-medium text-slate-900 dark:bg-yellow-500/30 dark:text-yellow-100'
                      : 'break-words text-slate-900 dark:text-gray-100'
                }
                title={updated.has(k) ? 'Updated by this trace' : undefined}
              >
                {value}
              </span>
            </div>
          );
        })}
        {rawExtras.map(([k, v]) => (
          <div key={`raw-${k}`} className="flex gap-1.5 py-0.5 text-[0.75rem]">
            <span className="min-w-[130px] shrink-0 text-right font-medium text-slate-500 dark:text-gray-400">
              {RAW_LABELS[k]}:
            </span>
            <span
              className={
                k === 'HAZ_CLASS' && v !== ''
                  ? 'break-words rounded bg-red-100 px-1 font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-200'
                  : 'break-words text-slate-900 dark:text-gray-100'
              }
              title={k === 'HAZ_CLASS' && v !== '' ? 'Terminal reports this container as hazardous' : undefined}
            >
              {v}
            </span>
          </div>
        ))}
      </div>
      {data.export_detail && <ExportDetail detail={data.export_detail} />}
      {(data.containers?.length ?? 0) > 0 && (
        <SectionTable
          title="Container Info"
          headers={['Equip #', 'Size', 'Type', 'Gate Out', 'Gate In']}
          rows={data.containers!.map((c) => [c.equipment, c.size, c.type, c.gateout, c.gatein])}
        />
      )}
      {data.raw != null && data.raw !== '' && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[0.75rem] font-semibold text-blue-600 dark:text-blue-400">
            Raw Results
          </summary>
          <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-slate-100 p-2 text-[0.6875rem] text-slate-800 dark:bg-gray-800 dark:text-gray-200">
            {/* POHA raw rides as an XML string — show it as-is, not
                JSON-escaped in quotes. Objects (gnosis) still pretty-print. */}
            {typeof data.raw === 'string' ? data.raw : JSON.stringify(data.raw, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default LegacyTracePanel;
